import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Copy, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { onLCP, onFCP, onTTFB, onCLS, onINP, type Metric } from "web-vitals";
import { getPerfSummary } from "@/lib/perfMetrics";
import { logger } from "@/lib/logger";

type Vitals = Partial<Record<"LCP" | "FCP" | "TTFB" | "CLS" | "INP", number>>;

const OVERLAY_KEY = "llx_perf_overlay_enabled";

const fmt = (name: string, v?: number) => {
  if (v == null) return "—";
  if (name === "CLS") return v.toFixed(3);
  return `${Math.round(v)}ms`;
};

const rating = (name: string, v?: number): "good" | "warn" | "bad" | "idle" => {
  if (v == null) return "idle";
  const t: Record<string, [number, number]> = {
    LCP: [2500, 4000],
    FCP: [1800, 3000],
    TTFB: [800, 1800],
    INP: [200, 500],
    CLS: [0.1, 0.25],
  };
  const [g, p] = t[name] ?? [Infinity, Infinity];
  if (v <= g) return "good";
  if (v >= p) return "bad";
  return "warn";
};

const ratingColor: Record<string, string> = {
  good: "text-emerald-400",
  warn: "text-amber-400",
  bad: "text-red-400",
  idle: "text-muted-foreground",
};

function useVitals(): Vitals {
  const [vitals, setVitals] = useState<Vitals>({});
  useEffect(() => {
    const update = (m: Metric) =>
      setVitals((prev) => ({ ...prev, [m.name]: m.value }));
    try {
      onLCP(update);
      onFCP(update);
      onTTFB(update);
      onCLS(update);
      onINP(update);
    } catch {
      /* ignore */
    }
  }, []);
  return vitals;
}

export const PerfOverlay = () => {
  const vitals = useVitals();
  return (
    <div
      className="fixed bottom-20 right-2 z-[9999] pointer-events-none rounded-md bg-black/80 px-2 py-1.5 text-[10px] font-mono leading-tight text-white shadow-lg backdrop-blur-sm border border-white/10"
      aria-hidden="true"
    >
      <div className="flex gap-2">
        {(["LCP", "FCP", "TTFB", "INP", "CLS"] as const).map((k) => (
          <span key={k} className={ratingColor[rating(k, vitals[k])]}>
            {k}:{fmt(k, vitals[k])}
          </span>
        ))}
      </div>
    </div>
  );
};

export const PerfDiagnosticsCard = () => {
  const vitals = useVitals();
  const [overlay, setOverlay] = useState(
    () => localStorage.getItem(OVERLAY_KEY) === "1"
  );

  useEffect(() => {
    localStorage.setItem(OVERLAY_KEY, overlay ? "1" : "0");
    window.dispatchEvent(new CustomEvent("llx-perf-overlay-changed"));
  }, [overlay]);

  const copyReport = async () => {
    const summary = getPerfSummary();
    const report = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
      connection: (navigator as any).connection
        ? {
            type: (navigator as any).connection.effectiveType,
            downlink: (navigator as any).connection.downlink,
            rtt: (navigator as any).connection.rtt,
          }
        : null,
      webVitals: vitals,
      coldStart: summary.coldStart,
      tabSwitches: summary.tabSwitches,
    };
    const text = JSON.stringify(report, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Perf report copied to clipboard");
    } catch {
      // Fallback: log to console
      logger.log("[PERF REPORT]\n" + text);
      toast.message("Clipboard blocked — report logged to console");
    }
  };

  return (
    <Card className="mb-4 border-purple-500/30 bg-gradient-to-br from-purple-950/40 to-violet-950/30">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Activity className="h-4 w-4 text-purple-400" />
          Performance Diagnostics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-5 gap-2 text-xs font-mono">
          {(["LCP", "FCP", "TTFB", "INP", "CLS"] as const).map((k) => (
            <div key={k} className="flex flex-col items-center rounded bg-background/40 p-2">
              <span className="text-[10px] text-muted-foreground">{k}</span>
              <span className={`font-bold ${ratingColor[rating(k, vitals[k])]}`}>
                {fmt(k, vitals[k])}
              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={copyReport} className="gap-1.5">
            <Copy className="h-3.5 w-3.5" />
            Copy perf report
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOverlay((v) => !v)}
            className="gap-1.5"
          >
            {overlay ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {overlay ? "Hide overlay" : "Show overlay"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug">
          Targets: LCP &lt; 1.5s (dashboard), Cold start &lt; 3s. Force-quit the app, relaunch,
          then tap <strong>Copy perf report</strong> when the dashboard is interactive.
        </p>
      </CardContent>
    </Card>
  );
};